import React from 'react';
import type { LearningPlanRecord } from '../../../../services/learningPlansService';
import { lessonInstancesService, type LessonInstanceRecord } from '../../../../services/lessonInstancesService';
import { jobsService, type JobRecord } from '../../../../services/jobsService';
import LessonView from '../Lessons/LessonView';

type LearningPlanDetailPageProps = {
  plan: LearningPlanRecord;
  onBack?: () => void;
  onDiscussWithTally?: (payload: { scenario: string; planId: string; lessonIndex: number; lessonInstance: LessonInstanceRecord }) => void;
};

const LearningPlanDetailPage: React.FC<LearningPlanDetailPageProps> = ({ plan, onBack, onDiscussWithTally }) => {
  const lessons: any[] = Array.isArray(plan?.plan?.lessons) ? plan.plan.lessons : [];
  const [activeLessonIndex, setActiveLessonIndex] = React.useState<number>(0);
  const [lessonInstance, setLessonInstance] = React.useState<LessonInstanceRecord | null>(null);
  const [loadingLesson, setLoadingLesson] = React.useState<boolean>(false);
  const [lessonError, setLessonError] = React.useState<string | null>(null);
  const [jobStatusLine, setJobStatusLine] = React.useState<string | null>(null);
  const [completionByIndex, setCompletionByIndex] = React.useState<Record<number, 'not_started' | 'in_progress' | 'completed'>>({});

  const title = plan?.title || plan?.plan?.title || 'Learning Plan';
  const planId = plan?.plan_id;

  const refreshCompletion = React.useCallback(async () => {
    if (!planId) return;
    try {
      const resp = await lessonInstancesService.list(planId);
      const map: Record<number, 'not_started' | 'in_progress' | 'completed'> = {};
      for (const it of resp.items || []) {
        const idx = (it as any)?.lesson_index;
        if (typeof idx === 'number') {
          const st = (it as any)?.completion?.status;
          if (st === 'completed' || st === 'in_progress' || st === 'not_started') {
            map[idx] = st;
          } else {
            map[idx] = 'not_started';
          }
        }
      }
      setCompletionByIndex(map);
    } catch {
      // Best-effort only; UI will default to not_started.
    }
  }, [planId]);

  React.useEffect(() => {
    refreshCompletion();
  }, [refreshCompletion]);

  const formatJobLine = (job: JobRecord) => {
    const bits: string[] = [];
    bits.push(job.status);
    if (job.phase) bits.push(job.phase);
    if (job.last_progress_event) bits.push(job.last_progress_event);
    if (job.attempts != null && job.max_attempts != null) bits.push(`attempt ${job.attempts}/${job.max_attempts}`);
    return bits.join(' · ');
  };

  const isFullyMaterialized = (li: LessonInstanceRecord | null | undefined) => {
    if (!li || typeof li !== 'object') return false;
    const lesson: any = (li as any).lesson;
    if (!lesson || typeof lesson !== 'object') return false;
    const sections: any[] = Array.isArray(lesson.sections) ? lesson.sections : [];
    if (sections.length < 1) return false;
    const exSections: any[] = Array.isArray((li as any).exercises?.sections) ? (li as any).exercises.sections : [];
    if (exSections.length < 1) return false;
    for (let i = 0; i < sections.length; i++) {
      const block = exSections.find((x: any) => x && typeof x === 'object' && x.section_index === i);
      const questions = Array.isArray(block?.questions) ? block.questions : [];
      if (questions.length < 1) return false;
    }
    return true;
  };

  React.useEffect(() => {
    let cancelled = false;

    const run = async () => {
      setLessonError(null);
      setLessonInstance(null);
      setJobStatusLine(null);

      if (!planId) {
        setLessonError('Missing plan_id');
        return;
      }
      if (!Array.isArray(lessons) || lessons.length === 0) {
        return;
      }
      if (activeLessonIndex < 0 || activeLessonIndex >= lessons.length) {
        setLessonError('Invalid lesson selection');
        return;
      }

      setLoadingLesson(true);
      try {
        // Try to load an existing instance first.
        const existing = await lessonInstancesService.get(planId, activeLessonIndex);
        if (cancelled) return;
        // If it's already fully materialized (lesson + exercises), render it.
        if (isFullyMaterialized(existing)) {
          setLessonInstance(existing);
          setJobStatusLine('ready');
        } else {
          // Treat partial legacy instances as not materialized; we'll regenerate in one job below.
          throw { status: 404, message: 'Lesson not materialized yet' };
        }
        // Mark in progress on first view.
        try {
          const current = existing?.completion?.status;
          if (current === 'not_started' || !current) {
            const updated = await lessonInstancesService.markViewed(planId, activeLessonIndex);
            if (!cancelled) {
              setLessonInstance(updated);
              setCompletionByIndex(prev => ({ ...prev, [activeLessonIndex]: 'in_progress' }));
            }
          } else if (current === 'in_progress' || current === 'completed') {
            setCompletionByIndex(prev => ({ ...prev, [activeLessonIndex]: current as any }));
          }
        } catch {}
        return;
      } catch (e: any) {
        if (cancelled) return;
        // If missing, materialize via async job.
        if (e?.status !== 404) {
          setLessonError(e?.message || 'Failed to load lesson');
          return;
        }
      } finally {
        if (!cancelled) setLoadingLesson(false);
      }

      // Submit generation job and poll.
      setLoadingLesson(true);
      try {
        const submit = await jobsService.submit(
          'lesson_materialize',
          { plan_id: planId, lesson_index: activeLessonIndex },
          { client: 'web' }
        );
        if (cancelled) return;
        setJobStatusLine(`queued · job_id=${submit.job_id}`);

        const finalJob = await jobsService.waitForCompletion(submit.job_id, {
          intervalMs: 1500,
          timeoutMs: 10 * 60 * 1000,
          onUpdate: (job) => {
            if (!cancelled) setJobStatusLine(formatJobLine(job));
          }
        });
        if (cancelled) return;

        if (finalJob.status !== 'succeeded') {
          setLessonError(typeof finalJob.error === 'string' ? finalJob.error : (finalJob.error?.message || `Lesson generation failed (${finalJob.status})`));
          return;
        }

        const inline = finalJob.result_inline;
        const li = inline?.lesson_instance;
        if (li && typeof li === 'object') {
          setLessonInstance(li as LessonInstanceRecord);
          // Generated -> still not_started until viewed; mark viewed now since we're showing it.
          try {
            const updated = await lessonInstancesService.markViewed(planId, activeLessonIndex);
            if (!cancelled) {
              setLessonInstance(updated);
              setCompletionByIndex(prev => ({ ...prev, [activeLessonIndex]: 'in_progress' }));
            }
          } catch {}
          return;
        }

        // Fallback: refetch instance (should exist if job succeeded).
        const fetched = await lessonInstancesService.get(planId, activeLessonIndex);
        if (cancelled) return;
        setLessonInstance(fetched);
        try {
          const updated = await lessonInstancesService.markViewed(planId, activeLessonIndex);
          if (!cancelled) {
            setLessonInstance(updated);
            setCompletionByIndex(prev => ({ ...prev, [activeLessonIndex]: 'in_progress' }));
          }
        } catch {}
      } catch (e: any) {
        if (cancelled) return;
        setLessonError(e?.message || 'Lesson generation failed');
      } finally {
        if (!cancelled) setLoadingLesson(false);
      }
    };

    run();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planId, activeLessonIndex]);

  const handleDiscussWithTally = async () => {
    setLessonError(null);
    if (!planId) {
      setLessonError('Missing plan_id');
      return;
    }
    if (!lessonInstance?.lesson) {
      setLessonError('Lesson not loaded yet');
      return;
    }
    const scenario =
      (lessonInstance.lesson?.talk_to_tally?.instructions && String(lessonInstance.lesson.talk_to_tally.instructions)) ||
      (lessonInstance.lesson?.talk_to_tally?.goal && `Let’s practice: ${String(lessonInstance.lesson.talk_to_tally.goal)}`) ||
      'Let’s practice this lesson.';

    let updatedLessonInstance: LessonInstanceRecord;
    try {
      updatedLessonInstance = await lessonInstancesService.markCompleted(planId, activeLessonIndex);
      setLessonInstance(updatedLessonInstance);
      setCompletionByIndex(prev => ({ ...prev, [activeLessonIndex]: 'completed' }));
    } catch (e: any) {
      setLessonError(e?.message || 'Failed to mark lesson completed');
      return;
    }

    try {
      onDiscussWithTally?.({ scenario, planId, lessonIndex: activeLessonIndex, lessonInstance: updatedLessonInstance! });
    } catch {}
  };

  return (
    <div className="bg-white rounded-lg shadow h-full overflow-hidden flex flex-col">
      <div className="border-b border-gray-200 py-6 px-6 bg-white flex items-center justify-between">
        <div className="flex items-center gap-3">
          {onBack ? (
            <button onClick={onBack} className="text-sm text-gray-600 hover:text-gray-800">Back</button>
          ) : null}
          <h1 className="text-2xl font-semibold text-gray-800">{title}</h1>
        </div>
        <div className="text-xs text-gray-500">{plan?.plan_id ? `plan_id: ${plan.plan_id}` : null}</div>
      </div>

      <div className="flex-1 min-h-0 grid grid-cols-1 md:grid-cols-[360px_1fr]">
        {/* Left: lessons list */}
        <div className="border-r border-gray-200 overflow-auto">
          <div className="p-4">
            <div className="text-sm font-semibold text-gray-800 mb-3">Lessons</div>
            {lessons.length === 0 ? (
              <div className="text-sm text-gray-600">No lessons found in this plan.</div>
            ) : (
              <div className="space-y-2">
                {lessons.map((l, idx) => {
                  const isActive = idx === activeLessonIndex;
                  const status = completionByIndex[idx] || 'not_started';
                  return (
                    <button
                      key={idx}
                      onClick={() => setActiveLessonIndex(idx)}
                      className="w-full text-left p-3 rounded-md border transition-colors"
                      style={{
                        borderColor: isActive ? '#042434' : '#e5e7eb',
                        backgroundColor: isActive ? 'rgba(4,36,52,0.06)' : 'white'
                      }}
                      title="Open lesson"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="text-sm font-semibold text-gray-900">{l?.title || `Lesson ${idx + 1}`}</div>
                        <div
                          className="text-[11px] px-2 py-0.5 rounded-full border"
                          style={{
                            borderColor: status === 'completed' ? '#16a34a' : status === 'in_progress' ? '#f59e0b' : '#e5e7eb',
                            color: status === 'completed' ? '#166534' : status === 'in_progress' ? '#92400e' : '#6b7280',
                            background: status === 'completed' ? '#dcfce7' : status === 'in_progress' ? '#fffbeb' : '#f9fafb',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {status === 'completed' ? 'Completed' : status === 'in_progress' ? 'In Progress' : 'Not Started'}
                        </div>
                      </div>
                      <div className="text-xs text-gray-600">
                        {typeof l?.estimated_minutes === 'number' ? `${l.estimated_minutes} min` : '15–20 min'}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Right: active lesson */}
        <div className="min-h-0 flex flex-col">
          {/* Scrollable lesson content */}
          <div className="flex-1 min-h-0 overflow-auto">
            <div className="p-6">
            <div className="text-sm font-semibold text-gray-800 mb-2">
              {lessons[activeLessonIndex]?.title ? lessons[activeLessonIndex].title : 'Lesson'}
            </div>
            <div className="text-gray-700">
              {jobStatusLine ? (
                <div className="text-xs text-gray-500 mb-3">Job: {jobStatusLine}</div>
              ) : null}

              {loadingLesson ? (
                <div className="text-sm text-gray-600">Loading / generating lesson…</div>
              ) : lessonError ? (
                <div className="text-sm text-red-700">Error: {lessonError}</div>
              ) : lessonInstance?.lesson ? (
                <LessonView
                  planTitle={title}
                  planId={planId || ''}
                  lessonIndex={activeLessonIndex}
                  lessonInstance={lessonInstance}
                  completionStatus={completionByIndex[activeLessonIndex] || 'not_started'}
                  showStickyFooter={true}
                  onDiscussWithTally={handleDiscussWithTally}
                />
              ) : (
                <div>
                  <div className="text-lg font-semibold mb-2">Coming Soon</div>
                  <div className="text-sm text-gray-600">
                    Click a lesson on the left to generate it on-demand.
                  </div>
                </div>
              )}
            </div>
          </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LearningPlanDetailPage;


